<template>
  <a-spin
    :spinning="isLoading"
    class="custom-spinner"
    style="height: 100vh; top: 20%"
  >
    <administrator-project-layout :model="model">
      <a-row class="administrator-project-form" :wrap="false">
        <a-col :span="5">
          <div class="administrator-project-form__menu">
            <router-link
              v-for="item in menuSteps"
              :key="`project-menu-` + item.step"
              :to="`#` + item.step"
              :class="{ active: item.step === step }"
            >
              <!-- <done-circle-icon :hide="item.hide" :completed="item.completed" /> -->
              {{ item.label }}
            </router-link>
          </div>
        </a-col>
        <a-col :span="19" class="administrator-project-form__content">
          <template v-if="step === 1">
            <administrator-project-form-step1 :project="model.project" />
          </template>
          <template v-if="step === 2">
            <administrator-project-form-step2 :project="model.project" />
          </template>
          <template v-if="step === 3">
            <administrator-project-form-step3 :project="model.project" />
          </template>
          <template v-if="step === 4">
            <administrator-project-form-step4 :project="model.project" />
          </template>
          <template v-if="step === 5">
            <administrator-project-form-step5 :project="model.project" />
          </template>
          <template v-if="step === 6">
            <administrator-project-form-step6 :project="model.project" />
          </template>
          <template v-if="step === 7">
            <administrator-project-form-step7 :project="model.project" />
          </template>
          <template v-if="step === 8">
            <administrator-project-form-step8 :project="model.project" />
          </template>
          <template v-if="step === 9">
            <administrator-project-form-step9 :project="model.project" />
          </template>
          <template v-if="step === 10">
            <administrator-project-form-step10
              :project="model.project"
              @update="handleUpdate"
            />
          </template>
        </a-col>
      </a-row>
    </administrator-project-layout>
  </a-spin>
</template>

<script setup>
  import { computed, onMounted, reactive, ref } from 'vue';
  import { useRoute } from 'vue-router';
  import { useApi } from '@/api/use-api';
  import AdministratorProjectLayout from '../components/AdministratorProjectLayout.vue';
  import AdministratorProjectFormStep1 from './steps/AdministratorProjectFormStep1.vue';
  import AdministratorProjectFormStep2 from './steps/AdministratorProjectFormStep2.vue';
  import AdministratorProjectFormStep3 from './steps/AdministratorProjectFormStep3.vue';
  import AdministratorProjectFormStep4 from './steps/AdministratorProjectFormStep4.vue';
  import AdministratorProjectFormStep5 from './steps/AdministratorProjectFormStep5.vue';
  import AdministratorProjectFormStep6 from './steps/AdministratorProjectFormStep6.vue';
  import AdministratorProjectFormStep7 from './steps/AdministratorProjectFormStep7.vue';
  import AdministratorProjectFormStep8 from './steps/AdministratorProjectFormStep8.vue';
  import AdministratorProjectFormStep9 from './steps/AdministratorProjectFormStep9.vue';
  import AdministratorProjectFormStep10 from './steps/AdministratorProjectFormStep10.vue';

  const api = useApi();
  const route = useRoute();
  const emit = defineEmits(['update']);

  const model = reactive({
    project: null,
    id: null,
    number: '',
    name: 'Новый проект',
    description: '',
    projectType: '',
    requestId: null,
    status: {
      type: 'draft',
      text: 'Черновик',
    },
    projectStatusId: null,
    approvals: [],
  });
  const isLoading = ref(true);

  const menuSteps = computed(() => {
    const steps = [
      { step: 1, label: 'Информация о проекте', hide: false, completed: true },
      { step: 2, label: 'Техническое задание', hide: false, completed: false },
      { step: 3, label: 'Календарный план', hide: false, completed: false },
      { step: 4, label: 'Команда проекта', hide: true, completed: false },
      { step: 5, label: 'Финансы', hide: true, completed: false },
      { step: 6, label: 'Ресурсы', hide: true, completed: false },
      {
        step: 7,
        label: 'Портфели и показатели',
        hide: false,
        completed: false,
      },
      { step: 8, label: 'Научные направления', hide: false, validate: true },
      { step: 9, label: 'Документы', hide: false, completed: false },
      { step: 10, label: 'Согласование', hide: true, completed: false },
    ];
    return model.approvals.length > 0 ? steps : steps.slice(0, -1);
  });

  const getModel = async () => {
    try {
      const response = await api.administratorPage.projectSteps(
        route.params.id
      );
      model.project = response.project;
      model.id = response.project.id;
      model.name = response.project.name;
      model.number = response.project.id;
      model.type = response.project.type;
      model.requestId = response.project.requestId;
      model.projectStatusId = response.project.projectStatusId;
      model.status = response.project.status;
      model.approvals = response.project.approvals;
    } catch (error) {
      console.error('error', error);
    }
  };

  onMounted(async () => {
    await getModel();
    isLoading.value = false;
  });

  const step = computed(() => {
    return route.hash ? Number(route.hash.replace('#', '')) : 1;
  });

  const handleUpdate = async () => {
    emit('update');
    await getModel();
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  h3 {
    color: $color-black;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 1.1;
  }

  .administrator-project-form {
    .administrator-project-form__menu {
      background: #f2f4f8;
      padding: 14px 6px;

      a {
        color: $color-text-primary;
        font-variant-numeric: slashed-zero;
        font-family: 'Nunito Sans', sans-serif;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 1.4;

        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
        margin: 6px 0;
        padding: 6px 6px 6px 30px;
        border-radius: 4px;

        &:hover,
        &.active {
          background: #ffffff;
          font-weight: 700;
        }

        :deep(.done-circle-icon) {
          margin-left: -24px;
          margin-right: 9px;
        }
      }
    }

    .administrator-project-form__content {
      padding-left: 20px;
    }
  }
</style>
